import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../DataTable';
import { useRowSpan } from '../DataTable/useRowSpan';
import PageHeaderActions from '../General/PageHeaderActions';
import moment from 'moment';
import * as MinistryReportApi from '../../api/MinistryReportApi';
import { toast } from 'react-toastify';
import IncidentListingModal from './IncidentListingModal';

export const MinistryIncidentCountTable = ({ isLoading, data }) => {
  const [rows, setRows] = useState([]);
  const DEFAULT_INCIDENT_DETAILS_MODAL = {
    center_id: null,
    show: false,
    title: 'Incident Details',
  };
  const [showIncidentDetailsModal, setShowIncidentDetailsModal] = useState(
    DEFAULT_INCIDENT_DETAILS_MODAL
  );

  const [modalData, setModalData] = useState([]);
  const categoryColors = {
    drivedownload: 'rgb(254, 195, 190)',
    contentdelivery: 'rgb(224,224,224)',
    contentprocess: 'rgb(255,224,143)',
    entryofcandidates: 'rgb(203,235,174)',
    registration: 'rgb(179,218,244)',
    aadharauthentication: 'rgb(187,211,218)',
    examcommencement: 'rgb(223,194,238)',
    allowedcandidateslogin: 'rgb(121,159,229)',
    examcompletion: 'rgb(144,132,219)',
    lockunlockincidents: 'rgb(122,234,203)',
  };

  // Extract all unique categories dynamically
  const allCategories = useMemo(
    () =>
      Array.from(
        new Set(data.flatMap((entry) => Object.keys(entry.incidents)))
      ),
    [data]
  );

  const [filter, setFilter] = useState({
    category: '',
    date: '',
  });

  // Transform data when `data` changes
  useEffect(() => {
    const transformedRows = data.map((entry) => {
      const row = { date: entry.date };

      allCategories.forEach((category) => {
        row[category] = entry.incidents[category] || 0; // Fill missing values with 0
      });

      return row;
    });

    setRows(transformedRows);
  }, [data, allCategories]);

  const fetchModalData = async () => {
    const response = await MinistryReportApi.getIncidentsByCategoryAndDate(
      filter
    );

    if (response?.success) {
      setModalData(response?.data);
    } else {
      toast.error(`Error fetching stage incidents:  ${response?.message}`);
    }
  };

  useEffect(() => {
    if (filter.category && filter.date) {
      // Prevent call when empty
      fetchModalData();
    }
  }, [filter]);

  const columns = useMemo(
    () => [
      {
        Header: 'Date',
        accessor: 'date',
        className: 'text-nowrap',
        disableSortBy: true,
        width: 100, // Set column width
        minWidth: 100,
        maxWidth: 100,
        Cell: ({ value }) => {
          return (
            <div
              style={{
                padding: '8px',
                borderRadius: '5px',
                minWidth: '100px',
                maxWidth: '100px',
                width: '100px',
              }}
            >
              {moment(value).format('DD/MM/YYYY')}
            </div>
          );
        },
      },
      ...allCategories.map((category) => ({
        Header: category,
        accessor: category,
        className: 'text-nowrap w-auto',
        disableSortBy: true,
        width: 150, // Set column width
        minWidth: 150,
        maxWidth: 150,
        Cell: ({ value, column, row }) => {
          // Normalize the category name dynamically
          const normalizedCategory = column.id
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '');

          const numericValue = Number(value);

          return (
            <div
              style={{
                backgroundColor:
                  (value > 0 && categoryColors[normalizedCategory]) ||
                  'transparent',
                padding: '8px',
                borderRadius: '5px',
                textAlign: 'center',
                minWidth: '165px',
                maxWidth: '165px',
                width: '165x',
                textDecoration:
                  typeof numericValue === 'number' && value > 0 && 'underline',
                cursor:
                  typeof numericValue === 'number' && value > 0 && 'pointer',
              }}
              onClick={() => {
                setFilter((prev) => ({
                  ...prev,
                  date: moment(row.original.date).format('YYYY/MM/DD'),
                  category: column.id,
                }));
                setShowIncidentDetailsModal((prev) => ({
                  ...prev,
                  show: true,
                }));
              }}
            >
              {value}
            </div>
          );
        },
      })),
    ],
    [allCategories]
  );

  return (
    <div>
      {rows.length > 0 ? (
        <DataTable
          // header={
          //   <div className='bg-light-rrb d-flex rounded-top-3 py-1'>
          //     <PageHeaderActions
          //       title='Stage of Exam'
          //       titleClassName={'custom-fs-16 fw-normal'}
          //       cardHeaderClassName={'border-0'}
          //     />
          //     <hr />
          //   </div>
          // }
          cardClass='border-0 shadow-none p-0 m-0'
          columns={columns}
          data={rows}
          totalRecords={rows.length}
          isLoading={isLoading}
          manual={true}
          hidePagination={true}
          tableHooks={(hooks) => {
            hooks.useInstance.push(useRowSpan);
          }}
          getHeaderProps={(column) => ({
            style: {
              minWidth: column.minWidth || '200px',
              maxWidth: column.maxWidth || '200px',
              width: column.width || '200px',
            },
          })}
          getCellProps={(cell) => ({
            style: {
              minWidth: cell.column.minWidth || '200px',
              maxWidth: cell.column.maxWidth || '200px',
              width: cell.column.width || '200px',
            },
          })}
          hideSearch
        />
      ) : (
        <div
          className='d-flex justify-content-center align-items-center'
          style={{ height: '100px' }}
        >
          <p className='m-0'>
            No data available, Please select exam day/shift filters to see past
            exam days data.
          </p>
        </div>
      )}
      {showIncidentDetailsModal.show && (
        <IncidentListingModal
          showIncidentDetailsModal={showIncidentDetailsModal}
          setShowIncidentDetailsModal={setShowIncidentDetailsModal}
          incidents={modalData}
          selectedCategory={filter?.selectedCategory}
        />
      )}
    </div>
  );
};
